import { catchWithLog } from 'helper/zod';
import { z } from 'zod';

import type { MissionEventType, MissionV2, VehicleV2 } from 'API';
import { ErrorTypeV2 } from 'API';
import {
    AdsStateFieldV2Schema,
    ConnectivityStatusFieldV2Schema,
    DeviationsFieldV2Schema,
    DriverModeFieldV2Schema,
    DrivingStateFieldV2Schema,
    MissionEventSchema,
    MissionProgressDetailsFieldV2Schema,
    MissionStateFieldV2Schema,
    MissionV2Schema,
    MrmStateFieldV2Schema,
    PositionFieldV2Schema,
    SpeedMsFieldV2Schema,
    StringFieldV2Schema,
    VehicleV2Schema,
} from 'APIzod';

/**
 * Utility type that enforces all keys of T are explicitly provided in a zod object shape,
 * preventing accidental omission of fields when constructing safe schema overrides.
 */
type ExactShape<T> = {
    [K in keyof T]-?: z.ZodType<T[K], any, any>;
};

/**
 * Builds a synthetic ErrorV2 with type PARSING_ERROR, used as a fallback when
 * client-side zod parsing fails on a V2 field value. This allows consumers to
 * distinguish a field that failed to parse from one that simply has no value.
 */
const parsingError = () => ({
    __typename: 'ErrorV2' as const,
    raisedAt: new Date().toISOString(),
    type: ErrorTypeV2.PARSING_ERROR,
});

const fieldParsingError = () => ({ __typename: undefined, value: null, error: parsingError(), ttl: null });

/**
 * Wraps a V2 field schema so that when the value fails to parse, the field is
 * preserved with value: null and a synthetic PARSING_ERROR injected into error.
 * If the entire field object is structurally broken, also returns a field with
 * value: null and PARSING_ERROR rather than null.
 */
const makeSafeFieldSchema = <
    T extends {
        __typename: z.ZodType<any>;
        error: z.ZodType<any>;
        ttl: z.ZodType<any>;
        value: z.ZodType<any>;
    },
>(
    shape: T,
    label: string
) => {
    const CATCH_SENTINEL = Symbol('catch');
    return z
        .object({
            __typename: shape.__typename,
            error: shape.error,
            ttl: shape.ttl,
            value: catchWithLog(shape.value, CATCH_SENTINEL as any, label),
        })
        .transform(field => {
            const wasCaught = field.value === CATCH_SENTINEL;
            return {
                ...field,
                value: wasCaught ? null : field.value,
                error: field.error ?? (wasCaught ? parsingError() : null),
            };
        });
};

const safeEntries = <T>(item: z.ZodType<T>, label: string) =>
    z
        .array(catchWithLog(item.nullable(), null, `${label}[]`))
        .transform(entries => entries.filter((entry): entry is T => entry !== null));

/** Required list. A list that is not even an array degrades to an empty list. */
const safeList = <T>(item: z.ZodType<T>, label: string): z.ZodType<T[]> =>
    catchWithLog(safeEntries(item, label), [], label);

/** Optional list. A broken list degrades to null; an absent list stays absent. */
const safeOptionalList = <T>(item: z.ZodType<T>, label: string) =>
    catchWithLog(safeEntries(item, label).nullish(), null, label);

/**
 * Sentinel used when the backend sends a mission event type this build does not
 * know yet. Mission events are rendered as a log, so an unrecognised entry is
 * kept and labelled "Unknown" rather than dropped — the surrounding entries
 * still carry meaning, and losing them would hide mission history.
 *
 * TODO: REST-SUBSCRIPTIONS: this sentinel only exists because the mission is still parsed
 * from GraphQL, where the parsed type IS the generated one. `APIzod.ts` builds its validator
 * straight off the codegen `MissionEventType`, and codegen will never add an unknown member
 * to it, so the value we need has no place in that type. That is what forces the two hacks
 * here: the hand-widened `MissionEventTypeOrUnknown`, and the
 * `as unknown as z.ZodType<MissionV2['events']>` cast in SafeMissionEventsSchema.
 *
 * `@control-center/control-center-client-public-missions-ts` already removes the need for both.
 * It ships a `MissionEvent` zod schema whose wire enum carries its own unknown value:
 * MISSION_EVENT_TYPE_UNSPECIFIED, MISSION_EVENT_TYPE_MISSION_ARCHIVED, and so on.
 *
 * What it does NOT ship is a replacement for the subscription — every one of its nine
 * endpoints is `responseFormat: "json"`, so live mission updates have to stay on AppSync.
 * That is the only reason this sentinel is still here.
 *
 * How to remove it, following what vehicles already do:
 * 1. Add a mission mapper next to `domain/mappers/vehicleFromRestV2.ts`, built on the
 *    library's `Mission` and `MissionEvent` zod schemas rather than on APIzod.
 * 2. Map the event type with that mapper's `fromEnum` helper. Strip the
 *    `MISSION_EVENT_TYPE_MISSION_` prefix and let anything unrecognised — UNSPECIFIED
 *    included — fall to 'UNKNOWN'. It returns a value instead of throwing.
 * 3. Declare the domain enum by hand in `domain/mission.ts` with 'UNKNOWN' as an ordinary
 *    member, the way `MrmState` does in `domain/vehicle.ts`. Never derive it from generated
 *    code, or the next codegen run silently changes what the app accepts.
 * 4. Feed the mission the way `vehiclesV2.ts` feeds a vehicle: REST
 *    (`GET /api/v2/missions/active/{vin}`) for the object, and the AppSync subscription
 *    patched on top, as `applyVehicleLiveUpdate` does.
 * 5. Then delete this sentinel, the widened type and the cast, and point
 *    MissionEventLogDisplayValue at the domain type. `UNKNOWN_MISSION_EVENT_TYPE` living in
 *    a zod helper and being imported by a display component is the smell this leaves behind.
 */
export const UNKNOWN_MISSION_EVENT_TYPE = 'UNKNOWN';

export type MissionEventTypeOrUnknown = MissionEventType | typeof UNKNOWN_MISSION_EVENT_TYPE;

/**
 * Safe version of MissionEventSchema.
 * An unrecognised `type` degrades to UNKNOWN_MISSION_EVENT_TYPE instead of failing
 * the event — and, because `events` is a required part of the mission object, the
 * whole mission along with it.
 */
const SafeMissionEventSchema = () => {
    const shape = MissionEventSchema().shape;
    return z.object({
        ...shape,
        type: catchWithLog(
            z.union([shape.type, z.literal(UNKNOWN_MISSION_EVENT_TYPE)]),
            UNKNOWN_MISSION_EVENT_TYPE,
            'MissionV2.events[].type'
        ),
    });
};

/**
 * Safe version of the mission `events` list.
 *
 * The cast is required because the widened `type` no longer matches MissionEvent
 * exactly. MissionEventLogDisplayValue accepts MissionEventTypeOrUnknown to cover
 * the widened value at the point where it is actually read.
 */
const SafeMissionEventsSchema = () =>
    safeOptionalList(SafeMissionEventSchema(), 'MissionV2.events') as unknown as z.ZodType<MissionV2['events']>;

// Safe top-level schemas — optional fields degrade gracefully on parse failure:
// the field is preserved with value: null and error.type = PARSING_ERROR injected,
// so consumers can distinguish a parse failure from a field with no value.

/**
 * Safe version of VehicleV2Schema.
 * Each optional field uses makeSafeFieldSchema so that a parse failure degrades
 * the value to null and injects a PARSING_ERROR, without affecting other fields
 * or other vehicles in the list.
 */
export const SafeVehicleV2Schema = (): z.ZodType<VehicleV2> => {
    const shape = VehicleV2Schema().shape;
    const safeShape: ExactShape<VehicleV2> = {
        __typename: shape.__typename,
        // `vin` is deliberately left strict. It is the identity of the vehicle: it is the cache key
        // in denormalizeListWithVin and the argument of every per-vehicle query, so a vehicle with a
        // broken vin cannot be addressed and there is no fallback that could be shown to the user.
        // Failing loudly is correct here, and SafeVehicleV2ListSchema keeps that failure contained to
        // the one entry instead of losing the whole list.
        vin: shape.vin,
        fleetId: catchWithLog(shape.fleetId, null, 'VehicleV2.fleetId'),
        licensePlate: catchWithLog(shape.licensePlate, null, 'VehicleV2.licensePlate'),
        // getVehicleIdentifier already falls back to licensePlate and then the vin when name is empty.
        name: catchWithLog(shape.name, '', 'VehicleV2.name'),
        timestamp: catchWithLog(shape.timestamp, '', 'VehicleV2.timestamp'),
        latestVehicleUpdate: catchWithLog(shape.latestVehicleUpdate, null, 'VehicleV2.latestVehicleUpdate'),
        owners: catchWithLog(shape.owners, [], 'VehicleV2.owners'),
        activeMissionId: catchWithLog(
            makeSafeFieldSchema(StringFieldV2Schema().shape, 'VehicleV2.activeMissionId').nullish(),
            fieldParsingError(),
            'VehicleV2.activeMissionId'
        ),
        adsState: catchWithLog(
            makeSafeFieldSchema(AdsStateFieldV2Schema().shape, 'VehicleV2.adsState').nullish(),
            fieldParsingError(),
            'VehicleV2.adsState'
        ),
        connectivityStatus: catchWithLog(
            makeSafeFieldSchema(ConnectivityStatusFieldV2Schema().shape, 'VehicleV2.connectivityStatus').nullish(),
            fieldParsingError(),
            'VehicleV2.connectivityStatus'
        ),
        deviations: catchWithLog(
            z
                .array(makeSafeFieldSchema(DeviationsFieldV2Schema().shape, 'VehicleV2.deviations[]').nullable())
                .nullish(),
            null,
            'VehicleV2.deviations'
        ),
        driverMode: catchWithLog(
            makeSafeFieldSchema(DriverModeFieldV2Schema().shape, 'VehicleV2.driverMode').nullish(),
            fieldParsingError(),
            'VehicleV2.driverMode'
        ),
        drivingState: catchWithLog(
            makeSafeFieldSchema(DrivingStateFieldV2Schema().shape, 'VehicleV2.drivingState').nullish(),
            fieldParsingError(),
            'VehicleV2.drivingState'
        ),
        mrmState: catchWithLog(
            makeSafeFieldSchema(MrmStateFieldV2Schema().shape, 'VehicleV2.mrmState').nullish(),
            fieldParsingError(),
            'VehicleV2.mrmState'
        ),
        position: catchWithLog(
            makeSafeFieldSchema(PositionFieldV2Schema().shape, 'VehicleV2.position').nullish(),
            fieldParsingError(),
            'VehicleV2.position'
        ),
        speedMs: catchWithLog(
            makeSafeFieldSchema(SpeedMsFieldV2Schema().shape, 'VehicleV2.speedMs').nullish(),
            fieldParsingError(),
            'VehicleV2.speedMs'
        ),
    };
    return z.object(safeShape) as unknown as z.ZodType<VehicleV2>;
};

/**
 * Safe version of a VehicleV2 list.
 * Use this instead of `z.array(SafeVehicleV2Schema())` anywhere a list is parsed. Each entry
 * is validated on its own, so one vehicle the client cannot parse is dropped and every other
 * vehicle still reaches the map. Parsing the array directly means one bad entry empties the
 * whole map, which is the failure the error handling guide exists to prevent.
 */
export const SafeVehicleV2ListSchema = (): z.ZodType<VehicleV2[]> => safeList(SafeVehicleV2Schema(), 'VehicleV2');

/**
 * Safe version of MissionV2Schema.
 * Each optional field uses makeSafeFieldSchema so that a parse failure degrades
 * the value to null and injects a PARSING_ERROR, without affecting other fields.
 */
export const SafeMissionV2Schema = (): z.ZodType<MissionV2> => {
    const shape = MissionV2Schema().shape;
    const safeShape: ExactShape<MissionV2> = {
        __typename: shape.__typename,
        // `id` and `vin` are deliberately left strict. They are the mission's identity: `id` is what
        // every mission mutation (start, cancel, archive, reset, assign) is addressed with, and `vin`
        // ties the mission to its vehicle. A degraded identity would let the UI send commands against
        // a mission that does not exist, which is worse than failing to show it.
        id: shape.id,
        vin: shape.vin,
        fleetId: catchWithLog(shape.fleetId, '', 'MissionV2.fleetId'),
        name: catchWithLog(shape.name, '', 'MissionV2.name'),
        description: catchWithLog(shape.description, '', 'MissionV2.description'),
        timestamp: catchWithLog(shape.timestamp, '', 'MissionV2.timestamp'),
        events: SafeMissionEventsSchema(),
        version: catchWithLog(shape.version, '', 'MissionV2.version'),
        route: catchWithLog(shape.route, null, 'MissionV2.route'),
        actualArrivalTime: catchWithLog(shape.actualArrivalTime, null, 'MissionV2.actualArrivalTime'),
        actualStartTime: catchWithLog(shape.actualStartTime, null, 'MissionV2.actualStartTime'),
        plannedArrivalTime: catchWithLog(shape.plannedArrivalTime, null, 'MissionV2.plannedArrivalTime'),
        progressDetails: catchWithLog(
            makeSafeFieldSchema(MissionProgressDetailsFieldV2Schema().shape, 'MissionV2.progressDetails').nullish(),
            fieldParsingError(),
            'MissionV2.progressDetails'
        ),
        state: catchWithLog(
            makeSafeFieldSchema(MissionStateFieldV2Schema().shape, 'MissionV2.state').nullish(),
            fieldParsingError(),
            'MissionV2.state'
        ),
    };
    return z.object(safeShape) as unknown as z.ZodType<MissionV2>;
};
